import { createEffect, onCleanup, onMount, reactive } from 'cdui-js';
import { addAccState, lastProgress, nextProgress } from './store';
import { saveApply, submitApply, composeSignatureOnAttachment } from '../../../../api/open-account';
import { showToast } from '../../components/Toast';
import { isH5User } from '../../../../stores/user';
import { blockBack } from '../../../../lib/browser';
import { clearAgreementPdfCache } from '../../../../lib/agreement-pdf-cache';
import i18n from '../../../../i18n';
import { fillSignatureBoard, paintSignatureHint } from '../paintSignatureHint';

/** 裁剪空白、逆时针旋转 90° 后导出签名 PNG（与开户签名逻辑一致，独立拷贝） */
const finalSignatureBase64 = (canvas: HTMLCanvasElement) => {
  const OUT_W = 400;
  const OUT_H = 100;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);

  const w = canvas.width;
  const h = canvas.height;
  const px = canvas.getContext('2d')!.getImageData(0, 0, w, h).data;
  let x0 = w,
    y0 = h,
    x1 = 0,
    y1 = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (px[i] < 245 || px[i + 1] < 245 || px[i + 2] < 245) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x0 > x1 || y0 > y1) return '';

  const pad = Math.round(12 * dpr);
  x0 = Math.max(0, x0 - pad);
  y0 = Math.max(0, y0 - pad);
  x1 = Math.min(w - 1, x1 + pad);
  y1 = Math.min(h - 1, y1 + pad);
  const srcW = x1 - x0 + 1;
  const srcH = y1 - y0 + 1;

  const fitScale = Math.min(OUT_W / (srcH / dpr), OUT_H / (srcW / dpr));
  const fitW = Math.round((srcH / dpr) * fitScale);
  const fitH = Math.round((srcW / dpr) * fitScale);
  const fitX = Math.round((OUT_W - fitW) / 2);
  const fitY = Math.round((OUT_H - fitH) / 2);

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = OUT_W;
  tempCanvas.height = OUT_H;
  const ctx = tempCanvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, OUT_W, OUT_H);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  ctx.save();
  ctx.translate(fitX, fitY + fitH);
  ctx.rotate(-Math.PI / 2);
  ctx.drawImage(canvas, x0, y0, srcW, srcH, 0, 0, fitH, fitW);
  ctx.restore();

  return tempCanvas.toDataURL('image/png', 0.9);
};

/** canvas dataURL → PNG File；强制 image/png，避免 iOS FormData 丢 MIME */
const dataURLtoFile = (dataUrl: string, filename: string): File => {
  const data = dataUrl.split(',')[1] || '';
  const binary = atob(data);
  const u8 = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) u8[i] = binary.charCodeAt(i);
  const blob = new Blob([u8], { type: 'image/png' });
  const name = filename.endsWith('.png') ? filename : `${filename}.png`;
  return new File([blob], name, { type: 'image/png' });
};

/** 签名区可用尺寸：相对 #h5-shell / 父容器，避免 PC 壳下 100vw/100vh 把按钮裁掉 */
const SIGNATURE_HEADER_OFFSET = 104;
const SIGNATURE_MOBILE_HEADER = 54;
/** 横屏上下留白（竖屏看是左右）；画板原先贴边 */
const SIGNATURE_EDGE_INSET = 12;

const getSignatureFallbackSize = () => {
  const shell = typeof document !== 'undefined' ? document.getElementById('h5-shell') : null;
  if (shell?.classList.contains('h5-shell--pc')) {
    return {
      width: shell.clientWidth || 430,
      height: Math.max(0, shell.clientHeight - SIGNATURE_MOBILE_HEADER),
    };
  }
  return {
    width: typeof window !== 'undefined' ? window.innerWidth : 390,
    height: typeof window !== 'undefined' ? Math.max(0, window.innerHeight - SIGNATURE_HEADER_OFFSET) : 500,
  };
};

const Signature = () => {
  const state = reactive({
    loading: false,
  });
  const layout = reactive(getSignatureFallbackSize());

  let canvasRef: HTMLCanvasElement | null = null;
  let rootRef: HTMLDivElement | null = null;
  let isDrawing = false;
  let ctx: CanvasRenderingContext2D | null = null;
  let lastX = 0;
  let lastY = 0;
  let hasStroke = false;

  const paintEmptyBoard = (cssWidth: number, cssHeight: number) => {
    if (!ctx) return;
    fillSignatureBoard(ctx, cssWidth, cssHeight);
    paintSignatureHint(ctx, cssWidth, cssHeight, i18n.AddAccount.SignHintPrefix);
  };

  const syncSignatureLayout = () => {
    const el = rootRef;
    const shell = document.getElementById('h5-shell');
    const width = el?.clientWidth || shell?.clientWidth || window.innerWidth;
    let height: number;
    if (el && shell) {
      height = Math.max(0, Math.round(shell.getBoundingClientRect().bottom - el.getBoundingClientRect().top));
    } else if (shell?.classList.contains('h5-shell--pc')) {
      height = Math.max(0, shell.clientHeight - SIGNATURE_MOBILE_HEADER);
    } else {
      height = Math.max(0, window.innerHeight - SIGNATURE_HEADER_OFFSET);
    }
    if (width > 0) layout.width = width;
    if (height > 0) layout.height = height;
  };

  createEffect(() => {
    const canvas = canvasRef;
    if (!canvas) return;
    void layout.width;
    void layout.height;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);

    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    hasStroke = false;
    paintEmptyBoard(rect.width, rect.height);
  });

  // 签名页锁滚：H5 Safari / iOS App WebView 均禁止跟手滚动与橡皮筋
  onMount(() => {
    const html = document.documentElement;
    const body = document.body;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      bodyOverflow: body.style.overflow,
      htmlOverflow: html.style.overflow,
      bodyTouchAction: body.style.touchAction,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverscroll: body.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
    };

    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    html.style.overscrollBehavior = 'none';
    body.style.overscrollBehavior = 'none';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.width = '100%';

    const preventScroll = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault();
    };
    const touchOpts: AddEventListenerOptions = { passive: false, capture: true };
    document.addEventListener('touchmove', preventScroll, touchOpts);

    let removeCanvasStyle: (() => void) | undefined;
    const bindCanvas = () => {
      const el = canvasRef;
      if (!el || removeCanvasStyle) return;
      el.style.touchAction = 'none';
      removeCanvasStyle = () => {
        el.style.touchAction = '';
      };
    };
    bindCanvas();
    const timer = window.setTimeout(bindCanvas, 0);

    syncSignatureLayout();
    const shell = document.getElementById('h5-shell');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncSignatureLayout()) : null;
    if (ro) {
      if (rootRef) ro.observe(rootRef);
      if (shell) ro.observe(shell);
    }
    window.addEventListener('resize', syncSignatureLayout);
    // ref 可能晚于 onMount，下一帧再量一次并挂观察
    const layoutTimer = window.setTimeout(() => {
      syncSignatureLayout();
      if (ro && rootRef) ro.observe(rootRef);
    }, 0);

    // H5：拦截 iOS Safari 左缘侧滑返回（App 不处理）
    const unlockBack = isH5User() ? blockBack() : undefined;

    onCleanup(() => {
      window.clearTimeout(timer);
      window.clearTimeout(layoutTimer);
      removeCanvasStyle?.();
      ro?.disconnect();
      window.removeEventListener('resize', syncSignatureLayout);
      document.removeEventListener('touchmove', preventScroll, touchOpts);
      unlockBack?.();
      body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      body.style.touchAction = prev.bodyTouchAction;
      html.style.overscrollBehavior = prev.htmlOverscroll;
      body.style.overscrollBehavior = prev.bodyOverscroll;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    });
  });

  const startDraw = (e: any) => {
    if (state.loading) return;
    if (e.cancelable) e.preventDefault();
    isDrawing = true;
    const { clientX, clientY } = e.touches ? e.touches[0] : e;
    const rect = canvasRef!.getBoundingClientRect();
    lastX = clientX - rect.left;
    lastY = clientY - rect.top;
    if (!hasStroke && ctx) {
      fillSignatureBoard(ctx, rect.width, rect.height);
      hasStroke = true;
    }
  };

  const drawing = (e: any) => {
    if (e.cancelable) e.preventDefault();
    if (state.loading || !isDrawing || !ctx || !canvasRef) return;
    const { clientX, clientY } = e.touches ? e.touches[0] : e;
    const rect = canvasRef.getBoundingClientRect();
    const currentX = clientX - rect.left;
    const currentY = clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(lastX, lastY);
    ctx.lineTo(currentX, currentY);
    ctx.stroke();

    lastX = currentX;
    lastY = currentY;
  };

  const endDraw = () => {
    isDrawing = false;
  };

  const clearCanvas = () => {
    if (state.loading) return;
    const canvas = canvasRef;
    if (!ctx || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    hasStroke = false;
    paintEmptyBoard(rect.width, rect.height);
  };

  const handleConfirm = async () => {
    if (state.loading) return;
    if (!canvasRef) return;

    const base64 = hasStroke ? finalSignatureBase64(canvasRef) : '';
    if (!base64) {
      showToast(i18n.AddAccount.ToastSignFirst);
      return;
    }

    isDrawing = false;
    state.loading = true;
    try {
      const data = await saveApply({
        applyType: 'ADD_ACCOUNT',
        securitiesAccountType: addAccState.securitiesAccountType,
        fundAccountType: addAccState.fundAccountType,
        needDerivativeTradePermission: addAccState.needDerivativeTradePermission,
      });
      addAccState.busId = data.id;

      const file = dataURLtoFile(base64, 'add_account_sign.png');

      await composeSignatureOnAttachment(
        addAccState.busId,
        file,
        'add_account_pdf_sign_completed',
      );

      await submitApply({ id: addAccState.busId });
      clearAgreementPdfCache();
      nextProgress();
    } catch (e) {
      showToast(e);
    } finally {
      state.loading = false;
    }
  };

  // const signName = addAccState.fullName;

  return (
    <div
      class="add-account-signature"
      ref={(el) => {
        rootRef = el;
      }}
      style={{
        position: 'relative',
        width: '100%',
        height: `${layout.height}px`,
        overflow: 'hidden',
        'touch-action': 'none',
        'overscroll-behavior': 'none',
        '-webkit-user-select': 'none',
        'user-select': 'none',
      }}
    >
      {/* 横屏操作区：相对壳宽高旋转，勿用 100vw（PC 壳远小于视口） */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: `${layout.height}px`,
          height: `${Math.max(0, layout.width - SIGNATURE_EDGE_INSET * 2)}px`,
          overflow: 'hidden',
          transform: 'translate(-50%, -50%) rotate(90deg)',
        }}
      >
        <div class="flex flex-column height-100">
          <div class="flex justify-center align-center gap-12px padding-x" style={{ height: '40px' }}>
            <button
              class="default-btn padding-x"
              style={{ height: '32px' }}
              onclick={() => clearCanvas()}
              disabled={state.loading}
            >
              {i18n.OpenProgress.Common.Rewrite}
            </button>
          </div>
          <div class="flex-auto"></div>
          <div class="flex justify-center">
            <div
              class="flex padding-x-L align-center"
              style={{ gap: '10px', height: '60px', width: `${Math.max(0, layout.width - SIGNATURE_EDGE_INSET * 2)}px` }}
            >
              <button
                class="default-btn btn-rounded"
                style={{ height: '48px', flex: 1 }}
                onclick={() => lastProgress()}
                disabled={state.loading}
              >
                {i18n.OpenProgress.Common.Prev}
              </button>
              <button
                class="confirm-btn btn-rounded"
                style={{ height: '48px', flex: 2 }}
                onclick={handleConfirm}
                disabled={state.loading}
              >
                {state.loading ? i18n.AddAccount.Submitting : i18n.Common.Confirm}
              </button>
            </div>
          </div>
        </div>
      </div>

      <canvas
        ref={(el) => (canvasRef = el)}
        style={{
          position: 'absolute',
          right: `${40 + SIGNATURE_EDGE_INSET}px`,
          top: 0,
          width: `calc(100% - 70px - 40px - ${SIGNATURE_EDGE_INSET * 2}px)`,
          height: `${layout.height}px`,
          'touch-action': 'none',
          'pointer-events': state.loading ? 'none' : 'auto',
        }}
        class="border"
        onmousedown={startDraw}
        onmousemove={drawing}
        onmouseup={endDraw}
        onmouseleave={endDraw}
        ontouchstart={startDraw}
        ontouchmove={drawing}
        ontouchend={endDraw}
      >
        {i18n.AddAccount.SignArea}
      </canvas>
    </div>
  );
};

export default Signature;
